import React, { useState, useEffect } from "react";
import { Concorrente } from "../types/concorrenti/concorrente";
import { ConcorrentiService } from "../services/concorrentiService";
import {
  DataGrid,
  DataGridColumn,
  DataGridAction,
  FormModal,
  FormField,
} from "../components";
import { ApiResponse } from "../types/apiResponse";

const UsersPage: React.FC = () => {
  const [concorrenti, setConcorrenti] = useState<ApiResponse<Concorrente>>({} as ApiResponse<Concorrente>);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [sortField, setSortField] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingConcorrente, setEditingConcorrente] =
    useState<Concorrente | null>(null);
  const [modalLoading, setModalLoading] = useState(false);

  // Debounce searchTerm
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 400);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // Funzione per caricare concorrenti dalla API
  const loadConcorrenti = async () => {
    try {
      setLoading(true);
      let orderBy: "FirstName" | "LastName" | "Email" | undefined = undefined;
      if (sortField === "firstName") orderBy = "FirstName";
      else if (sortField === "lastName") orderBy = "LastName";
      else if (sortField === "email") orderBy = "Email";
      const data = await ConcorrentiService.getConcorrentiList(
        page,
        pageSize,
        debouncedSearchTerm,
        orderBy,
        sortDirection === "desc"
      );
      setConcorrenti(data);
      setError(null);
    } catch (err) {
      setError("Errore nel caricamento dei concorrenti");
      console.error("Error loading concorrenti:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConcorrenti();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, debouncedSearchTerm, sortField, sortDirection]);

  const formFields: FormField<Concorrente>[] = [
    {
      key: "firstName",
      label: "Nome",
      type: "text",
      required: true,
      placeholder: "Inserisci il nome",
    },
    {
      key: "lastName",
      label: "Cognome",
      type: "text",
      required: true,
      placeholder: "Inserisci il cognome",
    },
    {
      key: "email",
      label: "Email",
      type: "email",
      required: false,
      placeholder: "Inserisci l'email",
    },
    { key: "isTroutFisher", label: "Trout Area", type: "checkbox", defaultValue: true },
    { key: "isCarpFisher", label: "Carpe e Carassi", type: "checkbox", defaultValue: false },
  ];

  const handleCreateConcorrente = () => {
    setEditingConcorrente(null);
    setIsModalOpen(true);
  };

  const handleEditConcorrente = (concorrente: Concorrente) => {
    setEditingConcorrente(concorrente);
    setIsModalOpen(true);
  };

  const handleDeleteConcorrente = async (concorrente: Concorrente) => {
    if (
      window.confirm(
        `Sei sicuro di voler eliminare ${concorrente.firstName} ${concorrente.lastName}?`
      )
    ) {
      try {
        await ConcorrentiService.deleteConcorrente(concorrente.id);
        await loadConcorrenti();
        console.log("Concorrente eliminato con successo");
        // TODO: Show success notification
      } catch (err) {
        console.error("Errore nell'eliminazione del concorrente:", err);
        // TODO: Show error notification
      }
    }
  };

  const handleFormSubmit = async (formData: Concorrente) => {
    try {
      setModalLoading(true);
      if (!formData.isTroutFisher && !formData.isCarpFisher) throw new Error("Abilita almeno una disciplina di pesca.");

      if (editingConcorrente) {
        // Update existing concorrente
        await ConcorrentiService.updateConcorrente(
          editingConcorrente.id,
          formData
        );
        console.log("Concorrente aggiornato con successo");
      } else {
        // Create new concorrente
        await ConcorrentiService.createConcorrente(
          formData
        );
        console.log("Concorrente creato con successo");
      }

      await loadConcorrenti();
      setIsModalOpen(false);
      // TODO: Show success notification
    } catch (err) {
      console.error("Error saving concorrente:", err);
      throw err;
    } finally {
      setModalLoading(false);
    }
  };

  const columns: DataGridColumn<Concorrente>[] = [
    {
      key: "firstName",
      label: "Nome Completo",
      sortable: true,
      render: (value, item) => `${item.firstName} ${item.lastName}`,
    },
    { key: "isTroutFisher", label: "Trout Area", sortable: false, render: value => value ? "Sì" : "No" },
    { key: "isCarpFisher", label: "Carpe e Carassi", sortable: false, render: value => value ? "Sì" : "No" },
    {
      key: "lastName",
      label: "Cognome",
      sortable: true,
      hidden: true, // Nascosto ma ricercabile
    },
    {
      key: "email",
      label: "Email",
      sortable: true,
      hidden: false,
    },
  ];

  const actions: DataGridAction<Concorrente>[] = [
    {
      label: "Modifica",
      onClick: handleEditConcorrente,
    },
    {
      label: "Elimina",
      onClick: handleDeleteConcorrente,
      variant: "danger",
    },
  ];

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleSortChange = (field: string, direction: "asc" | "desc") => {
    setSortField(field);
    setSortDirection(direction);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <h1 className="text-3xl font-bold text-gray-900">Concorrenti</h1>
        <button
          onClick={handleCreateConcorrente}
          className="bg-brand-700 hover:bg-brand-600 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
        >
          Aggiungi Concorrente
        </button>
      </div>

      {/* Data Grid */}
      <DataGrid
        data={concorrenti.results || []}
        columns={columns}
        actions={actions}
        searchPlaceholder="Cerca per nome, cognome o email..."
        searchTerm={searchTerm}
        onSearchTermChange={setSearchTerm}
        sortField={sortField}
        sortDirection={sortDirection}
        onSortChange={handleSortChange}
        emptyStateTitle="Nessun concorrente"
        emptyStateDescription="Inizia aggiungendo un nuovo concorrente."
        noResultsTitle="Nessun risultato"
        noResultsDescription="Prova a modificare i termini di ricerca."
        loading={loading}
        error={error}
        pageSize={pageSize}
        currentPage={page}
        totalPages={concorrenti.totalPages}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        showPagination={true}
      />

      {/* Form Modal */}
      <FormModal
        isOpen={isModalOpen}
        title={
          editingConcorrente ? "Modifica Concorrente" : "Nuovo Concorrente"
        }
        data={editingConcorrente}
        fields={formFields}
        checkboxGroupLabel="Discipline di pesca"
        checkboxGroupHint="Puoi abilitarle entrambe. Almeno una deve restare attiva."
        onSubmit={handleFormSubmit}
        onCancel={() => setIsModalOpen(false)}
        loading={modalLoading}
        submitLabel={editingConcorrente ? "Aggiorna" : "Crea"}
      />
    </div>
  );
};

export default UsersPage;
